import {
  Box,
  Card,
  Stack,
  TextField as MuiTextField,
  MenuItem,
  Grid,
  Chip,
  Button,
  TablePagination,
} from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import { Datagrid, TextField, FunctionField, useNotify, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from '../../../../../components/custom-breadcrumbs';
import { DashboardContent } from '../../../../../layouts/dashboard';
import { Resources } from '../../resource';
import { PermissionGuard } from '../../../../../routes/components/permission-guard';
import ErrorPage403 from '../../../../error/403';
import {
  getDailyPrizePoolStatusRange,
  DailyPrizePoolStatus,
  DailyPrizePoolStatusRangeFilter,
  PoolType,
} from '../../../services/redpacketService';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import { fNumber } from '../../../../../utils/format-number';

const DailyPrizePoolStatusList = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [data, setData] = useState<DailyPrizePoolStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ page: 0, perPage: 20 });
  const [filter, setFilter] = useState<DailyPrizePoolStatusRangeFilter>({});
  const [filterValues, setFilterValues] = useState<{
    startDate: Dayjs | null;
    endDate: Dayjs | null;
    poolType: PoolType | '';
  }>({
    startDate: null,
    endDate: null,
    poolType: '',
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const offset = pagination.page * pagination.perPage;
      const result = await getDailyPrizePoolStatusRange(filter, {
        limit: pagination.perPage,
        offset,
      });
      setData(result.records ?? []);
      setTotal(result.totalCount ?? 0);
    } catch (error: any) {
      notify(error.message || translate('module.redpacket.failedToGetData'), { type: 'error' });
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [pagination, filter, notify,translate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (field: string, value: any) => {
    setFilterValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilter = () => {
    // 按选中的日历日转成 UTC 当天，避免 toISOString() 因本地时区导致日期退一天
    const newFilter: DailyPrizePoolStatusRangeFilter = {
      startDate: filterValues.startDate
        ? `${filterValues.startDate.format('YYYY-MM-DD')}T00:00:00.000Z`
        : undefined,
      endDate: filterValues.endDate
        ? `${filterValues.endDate.format('YYYY-MM-DD')}T23:59:59.999Z`
        : undefined,
      poolType: filterValues.poolType || undefined,
    };
    setFilter(newFilter);
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPagination((prev) => ({ ...prev, perPage: parseInt(e.target.value, 10), page: 0 }));
  };

  return (
    <PermissionGuard resource="redpacket" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('module.redpacket.dailyPrizePoolStatus')}
          links={[
            { name: translate('module.redpacket.redPacketManagement'), href: `/${Resources.redpacket}` },
            { name: translate('module.redpacket.dailyPrizePoolStatus') },
          ]}
        />

        <Card sx={{ mt: 3, pb: 2 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label={translate('module.redpacket.startDate')}
                    value={filterValues.startDate}
                    onChange={(value) => handleFilterChange('startDate', value)}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label={translate('module.redpacket.endDate')}
                    value={filterValues.endDate}
                    onChange={(value) => handleFilterChange('endDate', value)}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MuiTextField
                    fullWidth
                    select
                    label={translate('module.redpacket.poolType')}
                    value={filterValues.poolType}
                    onChange={(e) => handleFilterChange('poolType', e.target.value)}
                    size="small"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value={PoolType.SHARED}>SHARED</MenuItem>
                    <MenuItem value={PoolType.CRITICAL_HIT}>CRITICAL_HIT</MenuItem>
                    <MenuItem value={PoolType.CRITICAL_HIT_DESIGNATED}>CRITICAL_HIT_DESIGNATED</MenuItem>
                  </MuiTextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Stack direction="row" spacing={2}>
                    <Button variant="contained" onClick={handleApplyFilter}>
                      {translate('module.redpacket.search')}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setFilterValues({ startDate: null, endDate: null, poolType: '' });
                        setFilter({});
                      }}
                    >
                      {translate('module.redpacket.reset')}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </LocalizationProvider>
          </Box>

          <Box sx={{ overflow: 'auto' }}>
            <Datagrid
              rowClick={false}
              data={data}
              isLoading={loading}
              total={total}
              bulkActionButtons={false}
            >
              {/*<TextField source="id" label="ID" sortable={false} />*/}
              <FunctionField
                label={translate('module.redpacket.date')}
                render={(record: DailyPrizePoolStatus) =>
                  record.date ? new Date(Number(record.date)).toLocaleDateString() : '-'
                }
              />
              <TextField source="poolType" label={translate('module.redpacket.poolType')} />
              <FunctionField
                label={translate('module.redpacket.totalBudgetStatus')}
                render={(record: DailyPrizePoolStatus) => fNumber(parseFloat(record.totalBudget || '0'))}
              />
              <FunctionField
                label={translate('module.redpacket.remaining')}
                render={(record: DailyPrizePoolStatus) => fNumber(parseFloat(record.remainingAmount || '0'))}
              />
              <FunctionField
                label={translate('module.redpacket.spent')}
                render={(record: DailyPrizePoolStatus) => fNumber(parseFloat(record.spentAmount || '0'))}
              />
              <TextField source="packetsIssued" label={translate('module.redpacket.packetsIssued')} />
              <TextField source="packetsClaimed" label={translate('module.redpacket.packetsClaimed')} />
              <FunctionField
                label={translate('module.redpacket.consumptionRateCol')}
                render={(record: DailyPrizePoolStatus) =>
                  typeof record.consumptionRate === 'number'
                    ? `${record.consumptionRate}%`
                    : `${record.consumptionRate ?? 0}%`
                }
              />
              <FunctionField
                label={translate('module.redpacket.available')}
                render={(record: DailyPrizePoolStatus) => (
                  <Chip label={record.isAvailable ? translate('module.redpacket.yes') : translate('module.redpacket.no')} color={record.isAvailable ? 'success' : 'default'} size="small" />
                )}
              />
              <FunctionField
                label={translate('module.redpacket.createdAtCol')}
                render={(record: DailyPrizePoolStatus) =>
                  record.createdAt ? new Date(record.createdAt).toLocaleString() : '-'
                }
              />
            </Datagrid>
          </Box>
          <TablePagination
            component="div"
            count={total}
            page={pagination.page}
            onPageChange={handlePageChange}
            rowsPerPage={pagination.perPage}
            onRowsPerPageChange={handlePerPageChange}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </Card>
      </DashboardContent>
    </PermissionGuard>
  );
};

export default DailyPrizePoolStatusList;
